import { NETWORK_ERROR_CODE } from '../services/network-error';
import { codiceErrore } from './codice-errore';

/**
 * Dal codice del server alla chiave i18n, per i TORNEI.
 *
 * Gemella di `arena-error-key.ts`, e nata per la stessa ragione — che quel file
 * dichiara da sé: «il server risponde con due cose, un `code` stabile e una frase
 * inglese scritta per chi legge i log. La frase resta nei log; a schermo va la
 * traduzione, scelta sul codice».
 *
 * Nei tornei quel patto non c'era. `tournament-store` scriveva in `error` la frase
 * del server (`setError(error.message)`) o una stringa inglese cablata
 * (`'Failed to load tournaments'`, `'Failed to join tournament'`, …), e le quattro
 * schermate la rendevano NUDA — `{error}`, senza `t()`. Il risultato: un giocatore
 * con l'app in polacco leggeva «Tournament not found» in mezzo a una pagina
 * polacca. `ArenaDetailPage` fa `{t(error)}`, con tanto di commento che dice
 * «`error` è una chiave i18n (vedi arena-store): la frase nasce qui».
 *
 * Codici diversi condividono una chiave quando per chi legge sono la stessa cosa:
 * `AUTH_REQUIRED` e `NOT_AUTHENTICATED` sono due punti del server, una cosa sola
 * da dire.
 */
export const TOURNAMENT_ERROR_KEYS: Readonly<Record<string, string>> = {
  /* --- dai gestori socket dei tornei (tournament.handler, swiss-handler) --- */
  AUTH_REQUIRED: 'tournament.errors.authRequired',
  NOT_AUTHENTICATED: 'tournament.errors.authRequired',
  GUEST_NOT_ALLOWED: 'tournament.errors.guestNotAllowed',
  NOT_ALLOWED: 'tournament.errors.notAllowed',
  TOURNAMENT_NOT_FOUND: 'tournament.errors.notFound',
  USER_NOT_FOUND: 'tournament.errors.userNotFound',
  INVALID_INPUT: 'tournament.errors.invalidInput',
  RATE_LIMITED: 'tournament.errors.rateLimited',
  /* La forma del rate limiter delle rotte REST: `{ error: 'RATE_LIMIT_EXCEEDED' }`
     invece di `{ code: 'RATE_LIMITED' }`. Due strade, una frase. */
  RATE_LIMIT_EXCEEDED: 'tournament.errors.rateLimited',
  JOIN_ERROR: 'tournament.errors.joinFailed',
  NOT_JOINABLE: 'tournament.errors.notJoinable',
  /* Lo SBARRAMENTO: il punteggio è sotto il minimo che il torneo chiede.
     Qui il server rispondeva `Rating must be between 1400 and 9999` — una frase
     inglese che questa mappa non conosceva, quindi finiva in «errore
     sconosciuto» proprio addosso a chi veniva escluso. Il numero non entra nel
     messaggio: questa mappa non porta parametri, e lo sbarramento si legge sulla
     pagina del torneo, prima di provare. */
  RATING_TOO_LOW: 'tournament.errors.ratingTooLow',
  /* Il turno è già partito: non si entra e non si esce. */
  /* Nati col ripiego lato server del blocco E: senza questi due, un rifiuto
     legittimo si leggeva «Qualcosa e' andato storto». */
  NOT_IN_TOURNAMENT: 'tournament.errors.notInTournament',
  ROUNDS_STARTED: 'tournament.errors.roundsStarted',
  /* Un evento Swiss mandato a un torneo arena-format, o viceversa. */
  WRONG_FORMAT: 'tournament.errors.wrongFormat',

  /* --- il cancello dei tornei privati, e la creazione --- */
  PRIVATE_TOURNAMENT: 'tournament.errors.privateTournament',
  WRONG_PASSWORD: 'tournament.errors.wrongPassword',
  TOO_MANY_ATTEMPTS: 'tournament.errors.tooManyAttempts',
  TOURNAMENT_FULL: 'tournament.errors.tournamentFull',
  ALREADY_HAS_LIVE_TOURNAMENT: 'tournament.errors.alreadyHasLive',
  NAME_TAKEN: 'tournament.errors.nameTaken',
  START_TOO_SOON: 'tournament.errors.startTooSoon',
  START_TOO_FAR: 'tournament.errors.startTooFar',
  NOT_ORGANIZER: 'tournament.errors.notOrganizer',
  ALREADY_STARTED: 'tournament.errors.alreadyStarted',
  /* Rifare le chiavi: un pubblico non ne ha, e la stessa password non chiude
     niente. Due rifiuti diversi, due frasi diverse — un messaggio solo per
     entrambi manderebbe a cercare la causa sbagliata. */
  NOT_PRIVATE: 'tournament.errors.notPrivate',
  SAME_PASSWORD: 'tournament.errors.samePassword',
  /* Annullato, non partito: due cause diverse per lo stesso rifiuto, e dirle
     uguali manda a guardare la cosa sbagliata. */
  TOURNAMENT_CANCELLED: 'tournament.errors.tournamentCancelled',
  /* La durata non copre i turni annunciati: si rifiuta invece di tagliarla. */
  TOURNAMENT_TOO_LONG: 'tournament.errors.tooLong',

  /* --- dal funnel generico (middleware/error-handler.ts) --- */
  /* Lo zod della creazione: un rifiuto di forma, che senza questa riga si
     leggeva «errore sconosciuto». */
  VALIDATION_ERROR: 'tournament.errors.invalidInput',
  INTERNAL_ERROR: 'tournament.errors.serverError',
  INTERNAL_SERVER_ERROR: 'tournament.errors.serverError',
  NOT_FOUND: 'tournament.errors.notFound',

  /* --- nati sul client: la richiesta non è mai arrivata al server --- */
  NOT_CONNECTED: 'tournament.errors.offline',
  /* `api.ts` lo lancia come `new Error(NETWORK_ERROR_CODE)`, quindi il codice sta in
     `.message` e non in `.code`: `codiceErrore` legge entrambe le forme. */
  [NETWORK_ERROR_CODE]: 'tournament.errors.offline',
  TIMEOUT: 'tournament.errors.timeout',
  /* `AckTimeoutError` di `services/socket.ts`: l'ack non è tornato entro il tetto. */
  AckTimeoutError: 'tournament.errors.timeout',
};

/** La chiave di ripiego: si dice qualcosa, mai niente. */
export const TOURNAMENT_UNKNOWN_ERROR_KEY = 'tournament.errors.unknown';

/**
 * La chiave i18n per un codice del server. Restituisce SEMPRE una chiave: un codice
 * nuovo, aggiunto lato server e non ancora tradotto, dà comunque una frase sensata
 * invece di una sigla in maiuscolo o di una casella vuota.
 */
export function tournamentErrorKey(code: string | undefined | null): string {
  if (!code) return TOURNAMENT_UNKNOWN_ERROR_KEY;
  return TOURNAMENT_ERROR_KEYS[code] ?? TOURNAMENT_UNKNOWN_ERROR_KEY;
}

/** La chiave i18n per un errore, qualunque delle tre forme abbia. */
export function tournamentErrorKeyFor(err: unknown): string {
  /* `AckTimeoutError` porta il codice nel `name`, non in `code` né in `message`. */
  if (err instanceof Error && err.name === 'AckTimeoutError') {
    return TOURNAMENT_ERROR_KEYS.AckTimeoutError!;
  }
  return tournamentErrorKey(codiceErrore(err));
}
